/**
 * Review Modal for CSV Import
 *
 * Modal dialog for reviewing and approving CSV import analysis results.
 * Manages internal step state: summary -> conflicts -> progress
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { FullscreenModal } from '@/lib/base/overlays/fullscreen-modal';
import { Dialog, Modal, ModalOverlay } from '@/lib/base/overlays/modal';
import {
  approveCsvImport,
  type ConflictResolutions,
  type CsvImport,
  type CsvImportStatus,
  getCsvImport,
  getFileResults,
  resolveConflicts,
  suggestCoercions,
} from '@/lib/csv-import/api';
import { logger } from '@/lib/logger';

import { CoercionReview } from './review/coercions/CoercionReview';
import { LargeImportCoercionReview } from './review/coercions/LargeImportCoercionReview';
import {
  hasStoredLargeImportCoercions,
  type ProbeRequestError,
} from './review/coercions/resumeProbe';
import { ConflictResolution } from './review/ConflictResolution';
import { ImportProgress } from './review/ImportProgress';
import { LargeImportSummary } from './review/LargeImportSummary';
import {
  computeImportTotals,
  getMatchColumnsRequiredFilenames,
  getReimportBlockEnabled,
} from './review/summary/types';
import { SummaryFooter, SummaryView } from './review/SummaryView';

// ============================================================================
// Types
// ============================================================================

export type ReviewStep = 'summary' | 'conflicts' | 'coercions' | 'progress';

interface ReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  orgId: number;
  importId: number;
  /**
   * Optional status hint from the caller (typically pulled off the
   * imports list item). Used to seed the initial `step` so the modal
   * renders at the correct width on first paint — without it, the
   * modal opens at the wide `sm:max-w-3xl` summary width and then
   * shrinks to `sm:max-w-md` once the load resolves and the effect
   * routes to the progress view. Optional: when omitted, the modal
   * falls back to the previous load-then-route behavior.
   */
  initialStatus?: CsvImportStatus;
  /**
   * Called when the user wants to go back and edit column mappings.
   * When provided, an "Edit Mappings" button is surfaced in the
   * summary footer. The parent is expected to close this modal and
   * open the wizard in resume mode.
   */
  onEditMappings?: () => void;
}

// ============================================================================
// Constants
// ============================================================================

const POLLING_INTERVAL_MS = 2000;

/**
 * Layout for the fullscreen review shell, matched to the wizard's review step
 * (step 3) so resuming an import from the list looks identical to reviewing it
 * inline. Both values mirror `CsvImportWizard`'s wide-step layout — keep them
 * in sync with `WIDE_STEP_MAX_CONTENT_WIDTH` and the wizard body wrapper in
 * `lib/base/wizard/wizard.tsx` if either changes.
 */
const REVIEW_CONTENT_MAX_WIDTH = 1100;
const REVIEW_CONTENT_PADDING = 'px-4 py-8 md:px-6';

// Statuses that route directly to the ImportProgress view rather than the
// summary/conflicts steps. Includes both in-flight ("analyzing", "importing")
// and final ("completed", "failed") states — the progress view branches on
// status to render the correct sub-state.
const PROGRESS_VIEW_STATUSES: readonly CsvImportStatus[] = [
  'importing',
  'completed',
  'failed',
  'analyzing',
];

// In-flight statuses that require polling to surface backend transitions.
const POLLABLE_STATUSES: readonly CsvImportStatus[] = [
  'importing',
  'analyzing',
];

// ============================================================================
// Component
// ============================================================================

export function ReviewModal({
  isOpen,
  onClose,
  orgId,
  importId,
  initialStatus,
  onEditMappings,
}: ReviewModalProps) {
  // Internal step state. Seeded from initialStatus when available so the
  // modal opens at the correct width on first paint — avoids the
  // wide-then-narrow resize jank that occurs when an analyzing/importing
  // import is opened from the list.
  const [step, setStep] = useState<ReviewStep>(() =>
    initialStatus && PROGRESS_VIEW_STATUSES.includes(initialStatus)
      ? 'progress'
      : 'summary'
  );

  // Import data state
  const [importData, setImportData] = useState<CsvImport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Action states
  const [isApproving, setIsApproving] = useState(false);
  const [isSuggesting, setIsSuggesting] = useState(false);
  const [coercionError, setCoercionError] = useState<string | null>(null);

  // Coercion footer (external-footer mode). CoercionReview publishes its current
  // apply handler into this ref and mirrors its derived button state here, so the
  // real Dialog footer renders the Back/Apply controls instead of them floating
  // inside the scrollable content.
  const coercionApplyRef = useRef<(() => void) | null>(null);
  const [coercionFooter, setCoercionFooter] = useState<{
    readyCount: number;
    isApplying: boolean;
  }>({ readyCount: 0, isApplying: false });
  const handleCoercionFooterState = useCallback(
    (state: { readyCount: number; isApplying: boolean }) => {
      setCoercionFooter(state);
    },
    []
  );
  const handleApplyCoercions = useCallback(() => {
    coercionApplyRef.current?.();
  }, []);

  // Conflict resolutions (managed by ConflictResolution component)
  const [conflictResolutions, setConflictResolutions] =
    useState<ConflictResolutions>({});

  // Polling ref
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );

  // Load import data on open
  useEffect(() => {
    if (!isOpen || !importId) {
      return;
    }

    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getCsvImport(orgId, importId);
        setImportData(data);

        // Sync step with the actual loaded status. In-flight / terminal
        // statuses route to the progress view; reviewable statuses
        // (e.g., 'ready_for_review') route to the summary. Setting this
        // unconditionally is important: a stale `initialStatus` hint
        // may have seeded step='progress', and without an explicit
        // 'summary' assignment here the modal would stay stuck on the
        // progress view even after data arrives showing it's reviewable.
        setStep(
          PROGRESS_VIEW_STATUSES.includes(data.status) ? 'progress' : 'summary'
        );
      } catch (err) {
        console.error('Failed to load import:', err);
        setError("We couldn't load this import. Try again.");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [isOpen, orgId, importId]);

  // Poll for progress while the import is in an in-flight status
  // (importing or analyzing). Without this, an analyzing import sits in the
  // progress view forever because the backend transition to 'awaiting_review'
  // / 'importing' / 'failed' is never observed.
  useEffect(() => {
    // Clear any existing polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    // Only poll if we have import data and status is in-flight
    if (!importData || !POLLABLE_STATUSES.includes(importData.status)) {
      return;
    }

    const pollStatus = async () => {
      try {
        const data = await getCsvImport(orgId, importId);
        setImportData(data);

        // Stop polling once the status is no longer in-flight
        if (!POLLABLE_STATUSES.includes(data.status)) {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          // If the status transitioned to a reviewable state (e.g.,
          // 'analyzing' -> 'ready_for_review'), route the user back to the
          // summary step. Without this, the modal stays on the progress view
          // until the user closes and reopens it.
          if (!PROGRESS_VIEW_STATUSES.includes(data.status)) {
            setStep('summary');
          }
        }
      } catch (err) {
        // Silently continue polling on error - don't interrupt progress view
        logger.error(
          'Failed to poll import status',
          err instanceof Error ? err : new Error(String(err)),
          { import_id: importId, org_id: orgId }
        );
      }
    };

    // Start interval
    pollingIntervalRef.current = setInterval(pollStatus, POLLING_INTERVAL_MS);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-poll when status changes, not on every importData field update
  }, [importData?.status, orgId, importId]);

  // Reset state when modal closes. Use the same step-seeding logic as
  // the initial useState so a subsequent reopen also lands on the right
  // step (and thus the right modal width).
  useEffect(() => {
    if (!isOpen) {
      setStep(
        initialStatus && PROGRESS_VIEW_STATUSES.includes(initialStatus)
          ? 'progress'
          : 'summary'
      );
      setImportData(null);
      setError(null);
      setConflictResolutions({});
      setCoercionError(null);
      setIsSuggesting(false);
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }
    // initialStatus is read inside the effect via closure; intentionally
    // omitted from deps so we only reset when the modal toggles open/closed,
    // not when a parent re-renders with a stale status snapshot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Handle approve and import
  const handleApprove = useCallback(async () => {
    if (!importId) {
      return;
    }

    setIsApproving(true);
    setError(null);

    try {
      // Submit conflict resolutions first if any exist
      if (Object.keys(conflictResolutions).length > 0) {
        await resolveConflicts(orgId, importId, conflictResolutions);
      }

      const data = await approveCsvImport(orgId, importId);
      setImportData(data);
      setStep('progress');
    } catch (err) {
      console.error('Failed to approve import:', err);
      setError(
        "We couldn't approve this import. Try again, and get in touch if it keeps happening."
      );
    } finally {
      setIsApproving(false);
    }
  }, [orgId, importId, conflictResolutions]);

  // Navigate to conflicts view
  const handleResolveConflicts = useCallback(() => {
    setStep('conflicts');
  }, []);

  // Go back to summary
  const handleBackToSummary = useCallback(() => {
    setStep('summary');
  }, []);

  // Handle close with completion check
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        handleClose();
      }
    },
    [handleClose]
  );

  // Handle retry after error
  const handleRetry = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const data = await getCsvImport(orgId, importId);
      setImportData(data);
      // Set unconditionally — a stale `initialStatus` hint may have seeded
      // step='progress', so a successful retry to a reviewable status must
      // explicitly route back to 'summary' or the modal stays stuck.
      setStep(
        PROGRESS_VIEW_STATUSES.includes(data.status) ? 'progress' : 'summary'
      );
    } catch (err) {
      console.error('Failed to load import:', err);
      setError("We couldn't load this import. Try again.");
    } finally {
      setLoading(false);
    }
  }, [orgId, importId]);

  // Totals for the sticky footer
  const totals = useMemo(
    () => computeImportTotals(importData?.analysis_result ?? null),
    [importData?.analysis_result]
  );

  // Large imports omit inline `analyzed_rows` from `analysis_result`, so the
  // per-row derivations below fall back to the aggregate totals / a safe
  // default instead of scanning rows that were never fetched.
  const isLargeImport = importData?.analyzed_rows_omitted === true;

  const matchColumnsRequiredFilenames = useMemo(
    () => getMatchColumnsRequiredFilenames(importData?.analysis_result ?? null),
    [importData?.analysis_result]
  );

  const reimportBlockEnabled = useMemo(
    () => getReimportBlockEnabled(importData?.analysis_result ?? null),
    [importData?.analysis_result]
  );

  const invalidAssociationsCount = useMemo(
    () =>
      importData?.analysis_result?.associations?.invalid_associations?.length ??
      0,
    [importData?.analysis_result]
  );

  const invalidRowCount = useMemo(() => {
    if (isLargeImport) {
      return totals.invalid;
    }
    const fileResults = getFileResults(importData?.analysis_result ?? null);
    let count = 0;
    for (const fileData of Object.values(fileResults)) {
      for (const row of fileData.analyzed_rows ?? []) {
        if (row.action === 'invalid') {
          count += 1;
        }
      }
    }
    return count;
  }, [importData?.analysis_result, isLargeImport, totals.invalid]);

  // True once the agent has run (proposals) or the user saved edits, so the
  // entry resumes the review instead of regenerating.
  const hasStoredCoercions = useMemo(() => {
    if (isLargeImport) {
      return false;
    }
    const fileResults = getFileResults(importData?.analysis_result ?? null);
    for (const fileData of Object.values(fileResults)) {
      for (const row of fileData.analyzed_rows ?? []) {
        if (row.action !== 'invalid') {
          continue;
        }
        if (
          (row.proposals && Object.keys(row.proposals).length > 0) ||
          (row.coercion_edits && Object.keys(row.coercion_edits).length > 0)
        ) {
          return true;
        }
      }
    }
    return false;
  }, [importData?.analysis_result, isLargeImport]);

  // File-id keys of the analyzed files, used to probe each file's invalid-row
  // working set for stored coercion work on a large import (see the probe in
  // `handleReviewCoercions`). Matches `LargeImportCoercionReview`'s own keys.
  const largeImportFileIds = useMemo(
    () => Object.keys(importData?.analysis_result?.file_summaries ?? {}),
    [importData?.analysis_result]
  );

  const handleReviewCoercions = useCallback(async () => {
    if (!importId) {
      return;
    }
    // Small imports: inline rows let `hasStoredCoercions` detect prior work
    // synchronously, so resume without spending another agent run.
    if (hasStoredCoercions) {
      setStep('coercions');
      return;
    }
    setIsSuggesting(true);
    setCoercionError(null);
    try {
      // Large imports omit inline `analyzed_rows`, so `hasStoredCoercions` is
      // always false for them and can't see a prior suggestion run. Probe each
      // file's invalid-row working set (the same rows the review loads): if any
      // already carry agent proposals or saved edits, resume the review instead
      // of regenerating (which wastes an agent run). A probe failure is
      // non-fatal — it falls through to a fresh suggestion, so a transient error
      // never blocks fixing rows.
      if (isLargeImport) {
        let alreadySuggested = false;
        try {
          alreadySuggested = await hasStoredLargeImportCoercions(
            orgId,
            importId,
            largeImportFileIds
          );
        } catch (probeErr) {
          const err =
            probeErr instanceof Error ? probeErr : new Error(String(probeErr));
          logger.error('Failed to probe stored coercions; regenerating', err, {
            import_id: importId,
            org_id: orgId,
            operation: 'probe',
            file_id: (err as ProbeRequestError).fileId,
          });
          setCoercionError(
            "Couldn't check for earlier work; generating fresh suggestions."
          );
        }
        if (alreadySuggested) {
          setStep('coercions');
          return;
        }
      }
      const updated = await suggestCoercions(orgId, importId);
      setImportData(updated);
      setStep('coercions');
    } catch (err) {
      logger.error(
        'Failed to suggest coercions',
        err instanceof Error ? err : new Error(String(err)),
        { import_id: importId, org_id: orgId }
      );
      setCoercionError(
        'Unable to generate suggestions right now. Please try again in a moment.'
      );
    } finally {
      setIsSuggesting(false);
    }
  }, [orgId, importId, hasStoredCoercions, isLargeImport, largeImportFileIds]);

  const handleCoercionsApplied = useCallback((updated: CsvImport) => {
    // The import is re-analyzing; the poll effect advances it back to a
    // reviewable status and routes to the summary.
    setImportData(updated);
    setConflictResolutions({});
    setStep('progress');
  }, []);

  // Render content based on step
  const renderContent = () => {
    if (loading) {
      return (
        <div className='flex items-center justify-center py-12'>
          <LoadingIndicator size='sm' label='Loading import data…' />
        </div>
      );
    }

    if (error) {
      return (
        <div className='p-4 bg-utility-error-50 border border-utility-error-200 rounded-lg'>
          <p className='text-sm text-utility-error-700'>{error}</p>
          <Button
            color='secondary'
            size='sm'
            className='mt-3'
            onClick={handleRetry}
          >
            Retry
          </Button>
        </div>
      );
    }

    if (!importData) {
      return (
        <div className='text-center py-12'>
          <p className='text-sm text-tertiary'>No import data available</p>
        </div>
      );
    }

    switch (step) {
      case 'summary':
        return (
          <>
            {coercionError && (
              <div
                role='alert'
                className='mb-4 p-3 bg-utility-error-50 border border-utility-error-200 rounded-lg'
              >
                <p className='text-sm text-utility-error-700'>
                  {coercionError}
                </p>
              </div>
            )}
            {invalidRowCount > 0 && (
              <div className='mb-4 flex items-center justify-between gap-4 rounded-lg border border-utility-error-200 bg-utility-error-50 p-4'>
                <p className='text-sm text-utility-error-700'>
                  {invalidRowCount} row{invalidRowCount === 1 ? '' : 's'}{' '}
                  couldn&apos;t be imported.{' '}
                  {hasStoredCoercions
                    ? 'Pick up where you left off reviewing the suggested fixes.'
                    : 'Let our agent suggest fixes you can review.'}
                </p>
                <Button
                  color='secondary'
                  size='sm'
                  onClick={handleReviewCoercions}
                  isLoading={isSuggesting}
                  isDisabled={isSuggesting}
                >
                  {hasStoredCoercions
                    ? 'Review suggested fixes'
                    : 'Let our agent fix these'}
                </Button>
              </div>
            )}
            {isLargeImport ? (
              <LargeImportSummary
                importData={importData}
                orgId={orgId}
                onApprove={handleApprove}
                onResolveConflicts={handleResolveConflicts}
                onEditMappings={onEditMappings}
                isApproving={isApproving}
                onReviewCoercions={
                  invalidRowCount > 0 ? handleReviewCoercions : undefined
                }
                coercionCtaLabel={
                  hasStoredCoercions
                    ? 'Review suggested fixes'
                    : 'Let our agent fix these'
                }
                isReviewingCoercions={isSuggesting}
              />
            ) : (
              <SummaryView
                importData={importData}
                orgId={orgId}
                onApprove={handleApprove}
                onResolveConflicts={handleResolveConflicts}
                onEditMappings={onEditMappings}
                isApproving={isApproving}
                externalFooter
                onReviewCoercions={
                  invalidRowCount > 0 ? handleReviewCoercions : undefined
                }
                coercionCtaLabel={
                  hasStoredCoercions
                    ? 'Review suggested fixes'
                    : 'Let our agent fix these'
                }
                isReviewingCoercions={isSuggesting}
              />
            )}
          </>
        );

      case 'coercions':
        return isLargeImport ? (
          <LargeImportCoercionReview
            importData={importData}
            orgId={orgId}
            importId={importId}
            onBack={handleBackToSummary}
            onApplied={handleCoercionsApplied}
          />
        ) : (
          <CoercionReview
            importData={importData}
            orgId={orgId}
            importId={importId}
            onBack={handleBackToSummary}
            onApplied={handleCoercionsApplied}
            showHeader={false}
            externalFooter
            applyControlsRef={coercionApplyRef}
            onFooterStateChange={handleCoercionFooterState}
          />
        );

      case 'conflicts':
        return (
          <ConflictResolution
            importData={importData}
            resolutions={conflictResolutions}
            onResolutionsChange={setConflictResolutions}
            onBack={handleBackToSummary}
            onApprove={handleApprove}
            isApproving={isApproving}
            orgId={orgId}
          />
        );

      case 'progress':
        return (
          <ImportProgress
            importData={importData}
            analysisTotal={totals.total}
          />
        );

      default:
        return null;
    }
  };

  // Effective status — prefer freshly-loaded importData but fall back to
  // the caller-supplied initialStatus on first render (importData is
  // still null then). This keeps the title from flickering from "Review
  // Import" to "Analyzing…" once data arrives.
  const effectiveStatus = importData?.status ?? initialStatus;

  // Get title based on step
  const title = useMemo(() => {
    switch (step) {
      case 'summary':
        return 'Review import';
      case 'conflicts':
        return 'Resolve conflicts';
      case 'coercions':
        return 'Review suggested fixes';
      case 'progress':
        switch (effectiveStatus) {
          case 'completed':
            return 'Import complete';
          case 'failed':
            return "We couldn't import your file";
          case 'analyzing':
            return 'Analyzing…';
          case 'importing':
          default:
            return 'Importing…';
        }
      default:
        return 'Review import';
    }
  }, [step, effectiveStatus]);

  // Show the import name as a sub-header on every step so users know which
  // import they're viewing, including on the progress/results screen.
  const description = importData?.name;

  // Large-import summary/coercion components render their own footers
  // inline, so the modal's separate footer must stay suppressed for them —
  // otherwise the controls would double up.
  const showSummaryFooter =
    step === 'summary' && importData && !loading && !error && !isLargeImport;

  const isTerminal =
    effectiveStatus === 'completed' || effectiveStatus === 'failed';

  const footer = useMemo(() => {
    if (showSummaryFooter) {
      return (
        <SummaryFooter
          totals={totals}
          onApprove={handleApprove}
          onResolveConflicts={handleResolveConflicts}
          onEditMappings={onEditMappings}
          isApproving={isApproving}
          matchColumnsRequiredFilenames={matchColumnsRequiredFilenames}
          reimportBlockEnabled={reimportBlockEnabled}
          invalidAssociationsCount={invalidAssociationsCount}
        />
      );
    }
    if (step === 'coercions' && !isLargeImport) {
      const { readyCount, isApplying } = coercionFooter;
      return (
        <div className='flex items-center justify-between'>
          <Button
            color='secondary'
            onClick={handleBackToSummary}
            isDisabled={isApplying}
          >
            Back
          </Button>
          <Button
            color='primary'
            onClick={handleApplyCoercions}
            isDisabled={readyCount === 0 || isApplying}
            isLoading={isApplying}
          >
            {readyCount > 0
              ? `Apply ${readyCount} fix${readyCount === 1 ? '' : 'es'}`
              : 'Apply fixes'}
          </Button>
        </div>
      );
    }
    if (step === 'progress' && isTerminal) {
      return (
        <div className='flex justify-end'>
          <Button color='primary' onClick={handleClose}>
            Done
          </Button>
        </div>
      );
    }
    return undefined;
  }, [
    handleApprove,
    handleClose,
    handleResolveConflicts,
    onEditMappings,
    isApproving,
    isLargeImport,
    isTerminal,
    showSummaryFooter,
    step,
    totals,
    matchColumnsRequiredFilenames,
    reimportBlockEnabled,
    invalidAssociationsCount,
    coercionFooter,
    handleBackToSummary,
    handleApplyCoercions,
  ]);

  // FullscreenModal takes a single `header` node rather than Dialog's separate
  // title/description props, so mirror the same two lines here.
  const header = useMemo(
    () => (
      <div>
        <h2 className='text-lg font-semibold text-[var(--color-text-primary)]'>
          {title}
        </h2>
        {description && (
          <p className='text-sm text-[var(--color-text-secondary)]'>
            {description}
          </p>
        )}
      </div>
    ),
    [title, description]
  );

  // Block close only during the synchronous approve-click handoff —
  // background statuses ('analyzing', 'importing') stay closable so the
  // user can move on with their work. The polling continues until the
  // modal unmounts; the list refreshes on close and shows the latest
  // status badge.
  const isCloseLocked = isApproving;

  // The progress view is a spinner plus a status line; a fullscreen shell would
  // dwarf it, so it keeps the compact modal.
  if (step === 'progress') {
    return (
      <ModalOverlay
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
        isDismissable={!isCloseLocked}
      >
        <Modal className='sm:max-w-md'>
          <Dialog
            isCloseDisabled={isCloseLocked}
            title={title}
            description={description}
            footer={footer}
          >
            {renderContent()}
          </Dialog>
        </Modal>
      </ModalOverlay>
    );
  }

  // Summary and conflicts render a wide data table per entity type, which
  // sm:max-w-3xl cramped badly. The import wizard that precedes this modal is
  // already fullscreen, so this keeps one continuous full-page experience
  // instead of dropping the user into a narrow dialog to review the result.
  return (
    <FullscreenModal
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      isDismissable={!isCloseLocked}
      // isDismissable only blocks overlay/keyboard dismissal; the header close
      // button is separate, so hide it too while approving to fully honor the
      // close lock (mirrors the progress Dialog's isCloseDisabled).
      hideCloseButton={isCloseLocked}
      dialogAriaLabel={title}
      instance='review-import'
      // Match the wizard's review step: same centered column width and the
      // same body padding, so the resumed review is visually identical to the
      // inline one. Without the padding the content sits flush against the
      // header divider.
      maxContentWidth={REVIEW_CONTENT_MAX_WIDTH}
      contentClassName={REVIEW_CONTENT_PADDING}
      header={header}
      footer={footer}
    >
      {renderContent()}
    </FullscreenModal>
  );
}
